import { useJsApiLoader } from '@react-google-maps/api'
import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { GOOGLE_API_KEY } from '../../../constants'
import { useFetchDeliveryRegionsQuery } from '../../../redux-toolkits/user/user.slice'
import {
  CreateDeliveryRegionPayload,
  DeliveryRegion,
  DeliveryRegionBoundary,
} from '../../../redux-toolkits/locations/locations.type'
import { Button } from '../../button'
import { TextInput } from '../../inputs/textInput'

const defaultPolygonCoordinates =
  '[[[3.3500,6.4500],[3.4200,6.4500],[3.4200,6.5200],[3.3500,6.5200],[3.3500,6.4500]]]'
const defaultMultiPolygonCoordinates =
  '[[[[3.3500,6.4500],[3.4200,6.4500],[3.4200,6.5200],[3.3500,6.5200],[3.3500,6.4500]]]]'
const googleMapsLibraries: 'places'[] = ['places']

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getRegionId(value?: string | DeliveryRegion) {
  if (!value) return ''
  return typeof value === 'string' ? value : value._id
}

function hasParentRegion(region: DeliveryRegion) {
  return !!getRegionId(region.parentRegion)
}

function buildViewportPolygon(viewport: google.maps.LatLngBounds) {
  const northEast = viewport.getNorthEast()
  const southWest = viewport.getSouthWest()
  const north = Number(northEast.lat().toFixed(6))
  const east = Number(northEast.lng().toFixed(6))
  const south = Number(southWest.lat().toFixed(6))
  const west = Number(southWest.lng().toFixed(6))

  return [
    [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ],
  ]
}

function buildFallbackPolygon(location: google.maps.LatLng) {
  const latitude = location.lat()
  const longitude = location.lng()
  const offset = 0.025
  const north = Number((latitude + offset).toFixed(6))
  const south = Number((latitude - offset).toFixed(6))
  const east = Number((longitude + offset).toFixed(6))
  const west = Number((longitude - offset).toFixed(6))

  return [
    [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ],
  ]
}

function getGoogleRegionQuery(name: string, parentName?: string) {
  const trimmedName = name.trim()
  const queryParts = [trimmedName]
  const normalizedName = trimmedName.toLowerCase()

  if (parentName && !normalizedName.includes(parentName.toLowerCase())) {
    queryParts.push(parentName)
  }

  const normalizedQuery = queryParts.join(', ').toLowerCase()
  if (!normalizedQuery.includes('lagos')) {
    queryParts.push('Lagos')
  }

  if (!normalizedQuery.includes('nigeria')) {
    queryParts.push('Nigeria')
  }

  return queryParts.join(', ')
}

function parseBoundary(
  type: DeliveryRegionBoundary['type'],
  coordinatesText: string,
) {
  try {
    const coordinates = JSON.parse(coordinatesText)

    if (!Array.isArray(coordinates) || coordinates.length < 1) {
      return { error: 'Enter valid GeoJSON coordinates.' }
    }

    return {
      boundary: {
        type,
        coordinates,
      },
    }
  } catch {
    return { error: 'Coordinates must be valid JSON.' }
  }
}

function flattenBoundaryPoints(boundary: DeliveryRegionBoundary) {
  if (boundary.type === 'Polygon') {
    return (boundary.coordinates as number[][][]).flat(1)
  }

  return (boundary.coordinates as number[][][][]).flat(2)
}

function removeClosingPoint(points: number[][]) {
  if (points.length < 2) return points

  const first = points[0]
  const last = points[points.length - 1]
  if (first[0] === last[0] && first[1] === last[1]) {
    return points.slice(0, -1)
  }

  return points
}

function getCoordinatePreview(
  type: DeliveryRegionBoundary['type'],
  coordinatesText: string,
) {
  const parsed = parseBoundary(type, coordinatesText)
  if (!parsed.boundary) return null

  const points = removeClosingPoint(
    flattenBoundaryPoints(parsed.boundary).filter(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        typeof point[0] === 'number' &&
        typeof point[1] === 'number',
    ),
  )

  if (points.length < 1) return null

  const totals = points.reduce(
    (acc, [longitude, latitude]) => ({
      longitude: acc.longitude + longitude,
      latitude: acc.latitude + latitude,
    }),
    { longitude: 0, latitude: 0 },
  )

  return {
    longitude: (totals.longitude / points.length).toFixed(6),
    latitude: (totals.latitude / points.length).toFixed(6),
    pointCount: points.length,
  }
}

export const AddDeliveryRegionModal = ({
  open,
  setOpen,
  loading,
  onCloseCallback = () => {},
  onSuccessCallback,
  region,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  loading?: boolean
  onCloseCallback?: () => void
  region?: DeliveryRegion
  onSuccessCallback: (
    payload: CreateDeliveryRegionPayload,
    resetForm: () => void,
  ) => void
}) => {
  const [name, setName] = useState('')
  const [boundaryType, setBoundaryType] =
    useState<DeliveryRegionBoundary['type']>('Polygon')
  const [coordinatesText, setCoordinatesText] = useState('')
  const [parentRegion, setParentRegion] = useState('')
  const [error, setError] = useState('')
  const [coordinateWarning, setCoordinateWarning] = useState('')
  const [fetchingCoordinates, setFetchingCoordinates] = useState(false)
  const [showAdvancedCoordinates, setShowAdvancedCoordinates] = useState(false)
  const [coordinateSource, setCoordinateSource] = useState<
    'default' | 'google' | 'manual'
  >('default')
  const generatedSlug = slugify(name)
  const coordinatePreview = getCoordinatePreview(boundaryType, coordinatesText)
  const { isLoaded: googleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: googleMapsLibraries,
    id: 'google-api-script',
  })
  const { data: regionsData } = useFetchDeliveryRegionsQuery(
    {
      page: 1,
      limit: 1000,
      isAvailable: true,
    },
    {
      skip: !open,
    },
  )

  const parentRegionOptions =
    regionsData?.data.docs.filter(
      (item) => item._id !== region?._id && !hasParentRegion(item),
    ) ?? []
  const selectedParentRegion = parentRegionOptions.find(
    (item) => item._id === parentRegion,
  )

  const resetForm = () => {
    setName('')
    setParentRegion('')
    setBoundaryType('Polygon')
    setCoordinatesText('')
    setError('')
    setCoordinateWarning('')
    setFetchingCoordinates(false)
    setShowAdvancedCoordinates(false)
    setCoordinateSource('default')
  }

  useEffect(() => {
    if (open && region) {
      setName(region.name)
      setParentRegion(getRegionId(region.parentRegion))
      setBoundaryType(region.boundary.type)
      setCoordinatesText(JSON.stringify(region.boundary.coordinates))
      setError('')
      setCoordinateWarning('')
      setCoordinateSource('manual')
      setShowAdvancedCoordinates(false)
      return
    }

    if (open) {
      resetForm()
      return
    }

    if (!open) {
      resetForm()
    }
  }, [open, region])

  const fetchCoordinatesFromGoogle = async () => {
    const regionName = name.trim()
    if (!regionName) {
      setError('Enter a region name first.')
      return
    }

    if (!googleMapsLoaded || !window.google?.maps) {
      setError('Google Maps is still loading. Try again in a moment.')
      return
    }

    setFetchingCoordinates(true)
    setError('')
    setCoordinateWarning('')

    const geocoder = new window.google.maps.Geocoder()

    try {
      const response = await geocoder.geocode({
        address: getGoogleRegionQuery(regionName, selectedParentRegion?.name),
        componentRestrictions: { country: 'NG' },
      })

      const result = response.results[0]
      const geometry = result?.geometry
      if (!geometry) {
        setError('No Google result found for this region name.')
        return
      }

      const coordinates = geometry.viewport
        ? buildViewportPolygon(geometry.viewport)
        : buildFallbackPolygon(geometry.location)

      setBoundaryType('Polygon')
      setCoordinatesText(JSON.stringify(coordinates))
      setCoordinateSource('google')
      setCoordinateWarning(
        `${result.formatted_address}${
          result.partial_match ? ' - partial Google match' : ''
        }`,
      )
    } catch {
      setError('Could not fetch coordinates from Google Maps.')
    } finally {
      setFetchingCoordinates(false)
    }
  }

  const handleSubmit = () => {
    const regionName = name.trim()
    if (!regionName) {
      setError('Region name is required.')
      return
    }

    const parsed = parseBoundary(boundaryType, coordinatesText)
    if (parsed.error || !parsed.boundary) {
      setError(parsed.error ?? 'Enter valid boundary coordinates.')
      return
    }

    setError('')
    onSuccessCallback(
      {
        name: regionName,
        slug: slugify(regionName),
        parentRegion: parentRegion || null,
        isAvailable: region?.isAvailable ?? true,
        boundary: parsed.boundary,
      },
      resetForm,
    )
  }

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={region ? 'Edit Delivery Region' : 'Add Delivery Region'}
      childrenClassName="md:!max-w-[520px]"
    >
      <div className="flex flex-col gap-4">
        <TextInput
          hasIcon={false}
          title="Region name"
          name="name"
          placeholder="Lagos Island"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />

        <div className="flex flex-col gap-3 rounded-[8px] border border-divider-gray p-3">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-black">
              Coordinates from Google
            </p>
            <p className="text-xs leading-5 text-sec-black">
              Use the region name to generate the boundary automatically. You
              can still edit the raw coordinates if needed.
            </p>
          </div>

          <Button
            label="Get coordinates"
            type="button"
            variant="border-grey"
            loading={fetchingCoordinates}
            disabled={fetchingCoordinates || !name.trim()}
            onClick={fetchCoordinatesFromGoogle}
          />
        </div>

        <TextInput
          hasIcon={false}
          title="Slug"
          name="slug"
          placeholder="lagos-island"
          value={generatedSlug}
          readOnly
          showAsReadOnly
        />

        <div className="flex flex-col gap-2">
          <p className="text-base font-medium text-black">Parent region</p>
          <select
            className="h-[48px] rounded-[8px] border border-divider-gray bg-white px-4 text-base text-black outline-none"
            value={parentRegion}
            onChange={(event) => {
              setParentRegion(event.target.value)
            }}
          >
            <option value="">None</option>
            {parentRegionOptions.map((item) => (
              <option key={item._id} value={item._id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        {coordinatePreview && (
          <div className="grid grid-cols-1 gap-2 rounded-[8px] border border-divider-gray p-3 md:grid-cols-4">
            <div className="md:col-span-4">
              <p className="text-sm font-medium text-black">
                {name.trim() || 'Region'} coordinates
              </p>
              <p className="mt-1 text-xs text-sec-black">
                {coordinateSource === 'google'
                  ? 'Generated from Google Maps'
                  : 'Using manually entered coordinates'}
              </p>
              {coordinateWarning && (
                <p className="mt-1 text-xs text-sec-black">
                  Google result: {coordinateWarning}
                </p>
              )}
            </div>
            <div className="rounded-[8px] bg-light-gray px-3 py-2">
              <span className="block text-[11px] text-sec-black">
                Longitude
              </span>
              <span className="block text-xs font-medium text-black">
                {coordinatePreview.longitude}
              </span>
            </div>
            <div className="rounded-[8px] bg-light-gray px-3 py-2">
              <span className="block text-[11px] text-sec-black">Latitude</span>
              <span className="block text-xs font-medium text-black">
                {coordinatePreview.latitude}
              </span>
            </div>
            <div className="rounded-[8px] bg-light-gray px-3 py-2">
              <span className="block text-[11px] text-sec-black">Boundary</span>
              <span className="block text-xs font-medium text-black">
                {boundaryType}
              </span>
            </div>
            <div className="rounded-[8px] bg-light-gray px-3 py-2">
              <span className="block text-[11px] text-sec-black">Points</span>
              <span className="block text-xs font-medium text-black">
                {coordinatePreview.pointCount}
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          className="w-fit text-sm font-medium text-black underline"
          onClick={() => {
            setShowAdvancedCoordinates((current) => !current)
          }}
        >
          {showAdvancedCoordinates
            ? 'Hide advanced coordinates'
            : 'Edit advanced coordinates'}
        </button>

        {showAdvancedCoordinates && (
          <div className="flex flex-col gap-4 rounded-[8px] border border-divider-gray p-3">
            <div className="flex flex-col gap-2">
              <p className="text-base font-medium text-black">Boundary type</p>
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-divider-gray p-1">
                {(['Polygon', 'MultiPolygon'] as const).map((type) => {
                  const active = boundaryType === type

                  return (
                    <button
                      key={type}
                      type="button"
                      className={`rounded-md px-4 py-3 text-sm transition-all ${
                        active ? 'bg-black text-white' : 'text-black'
                      }`}
                      onClick={() => {
                        const previousDefault =
                          boundaryType === 'Polygon'
                            ? defaultPolygonCoordinates
                            : defaultMultiPolygonCoordinates

                        setBoundaryType(type)

                        if (coordinatesText === previousDefault) {
                          setCoordinatesText(
                            type === 'Polygon'
                              ? defaultPolygonCoordinates
                              : defaultMultiPolygonCoordinates,
                          )
                        }
                      }}
                    >
                      {type}
                    </button>
                  )
                })}
              </div>
            </div>

            <TextInput
              hasIcon={false}
              title="Coordinates"
              name="coordinates"
              placeholder={defaultPolygonCoordinates}
              value={coordinatesText}
              onChange={(event) => {
                setCoordinatesText(event.target.value)
                setCoordinateSource('manual')
              }}
              type="textarea"
              rows={6}
            />
          </div>
        )}

        {error && <p className="text-danger text-sm">{error}</p>}

        <Button
          label="Proceed"
          type="button"
          className="mt-6"
          loading={loading}
          disabled={loading}
          primary
          onClick={handleSubmit}
        />
      </div>
    </ModalComponent>
  )
}
